import { Component } from '@theme/component';
import { ThemeEvents, VariantUpdateEvent, ZoomMediaSelectedEvent } from '@theme/events';

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });
  }

  #controller = new AbortController();

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller.abort();
  }

  /**
   * Handles a variant update event by replacing the current media gallery with a new one.
   *
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #handleVariantUpdate = (event) => {
    const source = event.detail.data.html;

    if (!source) return;
    const newMediaGallery = source.querySelector('media-gallery');

    if (!newMediaGallery) return;

    this.replaceWith(newMediaGallery);
  };

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  /**
   * Preloads an image.
   * @param {number} index - The index of the media to preload.
   */
  preloadImage(index) {
    const zoomDialogMedia = this.refs.zoomDialogComponent?.refs.media[index];
    if (!zoomDialogMedia) return;

    this.refs.zoomDialogComponent?.loadHighResolutionImage(zoomDialogMedia);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}

/* Keep video thumbnails visible beside an independently scrollable image-thumbnail rail. */
const pinnedVideoThumbnailStyles = document.createElement('style');
pinnedVideoThumbnailStyles.textContent = `
  @media screen and (max-width: 749px) {
    .go-floating-variants__inner,
    .go-floating-variants__list {
      justify-content: flex-start !important;
      min-width: 0;
    }

    .go-floating-variants__list {
      overflow-x: auto;
      overscroll-behavior-inline: contain;
      touch-action: pan-x;
      scrollbar-width: none;
      -webkit-overflow-scrolling: touch;
    }

    .go-floating-variants__list::-webkit-scrollbar {
      display: none;
    }

    .go-video-thumbnails-layout {
      display: flex;
      align-items: center;
      gap: 8px;
      width: 100%;
      min-width: 0;
    }

    .go-video-thumbnails-dock {
      display: flex;
      align-items: center;
      gap: inherit;
      flex: 0 0 calc(50% - 4px);
      width: calc(50% - 4px);
      min-width: 0;
      justify-content: flex-start;
      overflow-x: auto;
      overflow-y: hidden;
      scroll-snap-type: x proximity;
      touch-action: pan-x;
      overscroll-behavior-inline: contain;
      scrollbar-width: none;
      -webkit-overflow-scrolling: touch;
    }

    .go-video-thumbnails-dock::-webkit-scrollbar {
      display: none;
    }

    .go-video-thumbnails-dock > .go-ios-product__video-thumb {
      flex: 0 0 calc((100% - 8px) / 2);
      width: calc((100% - 8px) / 2) !important;
      min-width: 0;
      scroll-snap-align: start;
    }

    .go-video-thumbnails-layout > .go-ios-product__thumbs {
      flex: 0 0 calc(50% - 4px);
      width: calc(50% - 4px) !important;
      min-width: 0;
      justify-content: flex-start !important;
      overflow-x: auto !important;
      touch-action: pan-x;
      overscroll-behavior-inline: contain;
      -webkit-overflow-scrolling: touch;
    }
  }
`;
document.head.appendChild(pinnedVideoThumbnailStyles);

let pinVideoThumbnailsFrame;

function pinVideoThumbnails() {
  cancelAnimationFrame(pinVideoThumbnailsFrame);
  pinVideoThumbnailsFrame = requestAnimationFrame(() => {
    const useMobileLayout = window.matchMedia('(max-width: 749px)').matches;

    if (!useMobileLayout) {
      document.querySelectorAll('.go-video-thumbnails-layout').forEach((layout) => {
        const thumbnailRail = layout.querySelector(':scope > .go-ios-product__thumbs');
        const videoDock = layout.querySelector(':scope > .go-video-thumbnails-dock');

        if (!thumbnailRail || !videoDock) return;

        thumbnailRail.prepend(...videoDock.children);
        layout.before(thumbnailRail);
        layout.remove();
        thumbnailRail.scrollLeft = 0;
      });
    }

    document.querySelectorAll('.go-ios-product__thumbs').forEach((thumbnailRail) => {
      const videoThumbnails = thumbnailRail.querySelectorAll(':scope > .go-ios-product__video-thumb');

      if (!videoThumbnails.length) return;

      if (!useMobileLayout) {
        thumbnailRail.scrollLeft = 0;
        return;
      }

      const layout = document.createElement('div');
      const videoDock = document.createElement('div');

      layout.className = 'go-video-thumbnails-layout';
      videoDock.className = 'go-video-thumbnails-dock';

      thumbnailRail.before(layout);
      layout.append(videoDock, thumbnailRail);
      videoDock.append(...videoThumbnails);
    });
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', pinVideoThumbnails, { once: true });
} else {
  pinVideoThumbnails();
}

window.addEventListener('resize', pinVideoThumbnails, { passive: true });

const productGalleryObserver = new MutationObserver((mutations) => {
  if (mutations.some((mutation) => mutation.addedNodes.length > 0 || mutation.removedNodes.length > 0)) {
    pinVideoThumbnails();
  }
});

productGalleryObserver.observe(document.documentElement, {
  childList: true,
  subtree: true,
});

function getProductImageKey(image) {
  if (!image) return '';

  try {
    return new URL(image.currentSrc || image.src, window.location.origin).pathname;
  } catch {
    return image.currentSrc || image.src || '';
  }
}

function selectVariantThumbnail(imageKey) {
  if (!imageKey) return false;

  const thumbnailRail = document.querySelector('.go-ios-product__thumbs');
  if (!thumbnailRail) return false;

  const matchingThumbnail = Array.from(thumbnailRail.querySelectorAll('[data-go-ios-thumb]')).find(
    (thumbnail) => getProductImageKey(thumbnail.querySelector('img')) === imageKey
  );

  if (!matchingThumbnail) return false;

  if (
    !matchingThumbnail.classList.contains('is-active') &&
    matchingThumbnail.getAttribute('aria-current') !== 'true' &&
    matchingThumbnail.getAttribute('aria-selected') !== 'true'
  ) {
    matchingThumbnail.click();
  }

  requestAnimationFrame(() => {
    const railBounds = thumbnailRail.getBoundingClientRect();
    const thumbnailBounds = matchingThumbnail.getBoundingClientRect();
    const targetScrollLeft =
      thumbnailRail.scrollLeft +
      (thumbnailBounds.left - railBounds.left) -
      (thumbnailRail.clientWidth - thumbnailBounds.width) / 2;

    thumbnailRail.scrollTo({
      left: Math.max(0, targetScrollLeft),
      behavior: 'smooth',
    });
  });

  return true;
}

document.addEventListener('click', (event) => {
  const variantButton = event.target.closest('[data-go-floating-option-value]');
  if (!variantButton) return;

  const imageKey = getProductImageKey(variantButton.querySelector('img'));
  const retryDelays = [0, 100, 250, 500];

  retryDelays.forEach((delay) => {
    window.setTimeout(() => selectVariantThumbnail(imageKey), delay);
  });
});

function showSelectedFloatingVariant() {
  if (!window.matchMedia('(max-width: 749px)').matches) return;

  const selectedVariant = document.querySelector('.go-floating-variant.is-active');
  if (!selectedVariant) return;

  const variantList = selectedVariant.closest('.go-floating-variants__list');
  const variantInner = selectedVariant.closest('.go-floating-variants__inner');
  const scrollContainer = [variantList, variantInner].find(
    (element) => element && element.scrollWidth > element.clientWidth
  );

  if (!scrollContainer) return;

  requestAnimationFrame(() => {
    const containerBounds = scrollContainer.getBoundingClientRect();
    const variantBounds = selectedVariant.getBoundingClientRect();
    const targetScrollLeft =
      scrollContainer.scrollLeft + (variantBounds.left - containerBounds.left) - 8;

    scrollContainer.scrollTo({
      left: Math.max(0, targetScrollLeft),
      behavior: 'auto',
    });
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', showSelectedFloatingVariant, { once: true });
} else {
  showSelectedFloatingVariant();
}

window.addEventListener('resize', showSelectedFloatingVariant, { passive: true });

const floatingVariantObserver = new MutationObserver(showSelectedFloatingVariant);
floatingVariantObserver.observe(document.documentElement, {
  childList: true,
  subtree: true,
  attributes: true,
  attributeFilter: ['class'],
});
